import React, { useState } from 'react';
import './UnitOfferCalendar.scss';
import Card from '../../components/Card';
import { Button, Calendar, Badge, Modal, Table } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

const className = 'c-UnitOfferCalendar';

const getBadgeStyle = (startDateStr, courseOfferStatus) => {
  const [green, grey] = ['#81D25C', '#F0f0f0'];
  const isCanceledOrInactive =
    courseOfferStatus === 'Inactive' || courseOfferStatus === 'Cancelled';
  const startDate = new Date(startDateStr);
  const today = new Date();
  if (startDate < today || isCanceledOrInactive) {
    return { backgroundColor: grey, color: '#999' };
  }
  return { backgroundColor: green };
};

const getTiming = (localStartDate, localEndDate) => {
  const startDate = new Date(localStartDate);
  const endDate = new Date(localEndDate);

  const startTime = startDate
    .toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .toUpperCase();

  const endTime = endDate
    .toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .toUpperCase();

  const timeRange = `${startTime} - ${endTime}`;
  return timeRange;
};

const getModalTableData = (momentDt, calData) => {
  const tblCols = [
    {
      title: 'Title',
      dataIndex: 'unitDescription',
      key: 'unitDescription',
      render: (text, record) => (
        <Link to={`/unit-offer/${record.unitOfferId}`}>
          <p style={{ margin: 0 }}>{text}</p>
        </Link>
      ),
    },
    {
      title: 'Course Offer',
      dataIndex: 'courseOffer',
      key: 'courseOffer',
    },
    {
      title: 'Status',
      dataIndex: 'courseOfferStatus',
      key: 'courseOfferStatus',
      render: (status, record) => {
        if (status === 'Cancelled') {
          return <p className={`${className}__course-timing`}>{status}</p>;
        } else if (record.isCompleted) {
          return <p className={`${className}__course-timing`}>Completed</p>;
        }
        return <p className={`${className}__course-timing`}>{status}</p>;
      },
    },
    {
      title: 'Lesson time',
      dataIndex: 'timing',
      key: 'timing',
    },
  ];

  const [year, month, day] = [
    momentDt.year(),
    momentDt.month(),
    momentDt.date(),
  ];

  const dateUnitOfferData = calData?.[year]?.[month]?.[day] || [];
  const tblData = dateUnitOfferData.map((unitOffer) => {
    const {
      courseOffer,
      unitDescription,
      startDate,
      endDate,
      unitOfferId,
    } = unitOffer;
    let isCompleted = false;
    const lessonStartDate = new Date(startDate);
    const today = new Date();
    if (lessonStartDate < today) {
      isCompleted = true;
    }
    return {
      courseOffer: courseOffer.code,
      courseOfferStatus: courseOffer.courseOfferStatus,
      timing: getTiming(startDate, endDate),
      unitDescription,
      unitOfferId,
      isCompleted,
    };
  });
  return { tblCols, tblData };
};

const UnitOfferCalendar = ({ title, calData, onCalChange, courses }) => {
  // date for which the modal has to show data
  const [modalDateDetails, setModalDateDetails] = useState(null);
  const [showLegendModal, setShowLegendModal] = useState(false);

  const showDayDetailsModal = !!modalDateDetails;

  const openDayDetailsModal = (moment_dt) => setModalDateDetails(moment_dt);
  const closeDayDetailsModal = () => setModalDateDetails(null);

  const openLegendModal = () => setShowLegendModal(true);
  const closeLegendModal = () => setShowLegendModal(false);

  const renderDateCell = (value) => {
    const yr = value.year();
    const month = value.month();
    const day = value.date();
    const unitOffersList = calData?.[yr]?.[month]?.[day] || [];

    const handleCellDataClick = (e) => {
      e.stopPropagation();
      openDayDetailsModal(value);
    };

    const reducedList = unitOffersList.slice(0, 2);
    const moreCount = unitOffersList?.length - reducedList?.length;

    return (
      <div onClick={handleCellDataClick}>
        {reducedList.map((unitOffer, index) => {
          const { unitDescription, courseOffer, startDate } = unitOffer;
          const badgeContent = `${unitDescription} (Course: ${courseOffer?.code})`;
          return (
            <Badge
              count={badgeContent}
              style={getBadgeStyle(startDate, courseOffer.courseOfferStatus)}
              key={index}
            />
          );
        })}
        {moreCount > 0 && (
          <div className={`${className}__load__more`}>+{moreCount}</div>
        )}
      </div>
    );
  };

  const renderDayDetailsModalContent = () => {
    if (!modalDateDetails) return null;
    const [year, month, day] = [
      modalDateDetails.year(),
      modalDateDetails.month(),
      modalDateDetails.date(),
    ];
    const titleDate = modalDateDetails.format('DD MMM YYYY (dddd)');
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzOffset = modalDateDetails.format('Z z');
    const timeZone = `${tz} ${tzOffset}`;

    const { tblCols, tblData } = getModalTableData(modalDateDetails, calData);
    return (
      <div
        className={`${className}__modal-inner ${className}__day-details-modal-inner`}
      >
        <div className={`${className}__modal-title-container`}>
          <p className={`${className}__modal-title`}>{titleDate}</p>
          <p className={`${className}__day-details-tz`}>{timeZone}</p>
        </div>
        <div>
          <Table columns={tblCols} dataSource={tblData} pagination={false} />
        </div>
      </div>
    );
  };

  const renderLegendModalContent = () => {
    const tblCols = [
      {
        title: 'Course Code',
        dataIndex: 'value',
        key: 'value',
      },
      {
        title: 'Course Name',
        dataIndex: 'label',
        key: 'label',
      },
    ];

    return (
      <div className={`${className}__modal-inner`}>
        <Table columns={tblCols} dataSource={courses} pagination={false} />
      </div>
    );
  };

  const renderColorLegend = () => {
    const colorMap = [
      {
        color: '#81D25C',
        label: 'Active and fully filled',
      },
      {
        color: '#F4BE53',
        label: 'Active and >=50% filled',
      },
      {
        color: '#FF4D4F',
        label: 'Active and <50% filled',
      },
      {
        color: '#F0f0f0',
        label: 'Inactive/Cancelled/Completed',
      },
    ];

    const tblCols = [
      {
        title: 'Color',
        dataIndex: 'color',
        key: 'color',
        render: (color) => {
          return (
            <div
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: color,
                borderRadius: '100%',
              }}
            />
          );
        },
      },
      {
        title: 'Label',
        dataIndex: 'label',
        key: 'label',
      },
    ];

    return (
      <div className={`${className}__modal-inner`}>
        <div className={`${className}__modal-title-container`}>
          <p className={`${className}__modal-title`}>Legend</p>
        </div>
        <div>
          <Table columns={tblCols} dataSource={colorMap} pagination={false} />
        </div>
      </div>
    );
  };

  return (
    <div className={className}>
      <Card>
        <div className={`${className}__flex-container`}>
          <div className={`${className}__title`}>{title}</div>
          <div className={`${className}__legend`}>
            {courses.length > 0 && (
              <Button
                ghost
                type="primary"
                icon={<InfoCircleOutlined />}
                onClick={openLegendModal}
              >
                Legend
              </Button>
            )}
          </div>
        </div>
        <div className={`${className}__cal-container`}>
          <Calendar dateCellRender={renderDateCell} onChange={onCalChange} />
        </div>
      </Card>

      {/* Day Details Modal */}
      <Modal
        visible={showDayDetailsModal}
        onCancel={closeDayDetailsModal}
        footer={null}
      >
        {renderDayDetailsModalContent()}
      </Modal>

      {/* Legend Modal */}
      {courses.length > 0 && (
        <Modal
          visible={showLegendModal}
          onCancel={closeLegendModal}
          footer={null}
        >
          {renderColorLegend()}
          {renderLegendModalContent()}
        </Modal>
      )}
    </div>
  );
};

export default UnitOfferCalendar;
